import { v4 as uuidv4 } from 'uuid';
import { getConsentData } from './getConsentModeData';

export const CORRELATION_STORAGE_KEY = 'bx-login-correlation-id';
const REPORTED_PREFIX = 'bx-login-reported:';
const UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const MAX_AGE_MS = 24 * 60 * 60 * 1000;
// authenticated_at comes from G2's clock, which this browser's clock may lag.
const CLOCK_SKEW_MS = 5 * 60 * 1000;
let memoryId: string | undefined;
let persisted = false;
const reported = new Set<string>();

// unified_login: an interactive G2 login. sso: G2 authorized silently because
// the user was already signed in to G2 (e.g. through another partner brand).
const LOGIN_METHODS = ['unified_login', 'sso'] as const;
export type UnifiedLoginMethod = (typeof LOGIN_METHODS)[number];

export type UnifiedLoginOutcome = {
  attemptId: string;
  method: UnifiedLoginMethod;
  correlationId: string;
  site: string;
  userId: string;
  isSignUp: boolean;
  authenticatedAt: number;
  /** G2's upstream OmniAuth provider, e.g. 'google_oauth2' or 'linkedin'. */
  provider?: string;
};

const storage = (): Storage | null => {
  if (typeof window === 'undefined') return null;
  try {
    return getConsentData(window)?.analytics_storage === 'G'
      ? window.localStorage
      : null;
  } catch {
    return null;
  }
};

export const getLoginCorrelationId = (): string => {
  if (typeof window === 'undefined') return uuidv4();
  const target = storage();
  try {
    const existing = target?.getItem(CORRELATION_STORAGE_KEY);
    if (existing && UUID.test(existing)) {
      memoryId = existing;
      persisted = true;
      return existing;
    }
    if (target && persisted) {
      memoryId = undefined;
      persisted = false;
    }
  } catch {
    // Browser storage must never block authentication.
  }
  memoryId ||= uuidv4();
  try {
    target?.setItem(CORRELATION_STORAGE_KEY, memoryId);
    persisted = target !== null;
  } catch {
    // Keep this document's fallback when storage is unavailable.
  }
  return memoryId;
};

// Only the protected userinfo claim is mapped here, never URL parameters.
export const getUnifiedLoginOutcome = (
  claim: unknown,
  userId: unknown
): UnifiedLoginOutcome | null => {
  if (!claim || typeof claim !== 'object') return null;
  const value = claim as Record<string, unknown>;
  if (
    !LOGIN_METHODS.includes(value.method as UnifiedLoginMethod) ||
    typeof value.attempt_id !== 'string' ||
    !value.attempt_id ||
    typeof value.user_custom_session_id !== 'string' ||
    !UUID.test(value.user_custom_session_id) ||
    typeof value.site !== 'string' ||
    !['capterra', 'getapp', 'software_advice'].includes(value.site) ||
    typeof userId !== 'string' ||
    !userId ||
    typeof value.is_sign_up !== 'boolean' ||
    typeof value.authenticated_at !== 'number' ||
    !Number.isFinite(value.authenticated_at)
  )
    return null;
  return {
    attemptId: value.attempt_id,
    method: value.method as UnifiedLoginMethod,
    correlationId: value.user_custom_session_id,
    site: value.site,
    userId,
    isSignUp: value.is_sign_up,
    authenticatedAt: value.authenticated_at,
    ...(typeof value.provider === 'string' && value.provider
      ? { provider: value.provider }
      : {}),
  };
};

export const consumeUnifiedLogin = (
  outcome: UnifiedLoginOutcome | null | undefined,
  site: string,
  now = Date.now()
): boolean => {
  if (!outcome || outcome.site !== site) return false;
  const age = now - outcome.authenticatedAt * 1000;
  if (
    age < -CLOCK_SKEW_MS ||
    age > MAX_AGE_MS ||
    reported.has(outcome.attemptId)
  )
    return false;
  const key = `${REPORTED_PREFIX}${outcome.attemptId}`;
  // The claim stays in the session for the JWT lifetime, so new tabs see it
  // too. Retain only a per-attempt boolean in this tab and across tabs, even
  // before consent initializes; correlation identity remains consent-gated.
  try {
    if (typeof window !== 'undefined' && window.sessionStorage.getItem(key))
      return false;
  } catch {
    // Persistent storage or the document guard may still be available.
  }
  try {
    if (typeof window !== 'undefined' && window.localStorage.getItem(key))
      return false;
  } catch {
    // A blocked persistent store must not bypass the session guard.
  }
  reported.add(outcome.attemptId);
  try {
    if (typeof window !== 'undefined') window.sessionStorage.setItem(key, '1');
  } catch {
    // The persistent and document guards still prevent duplicate effects.
  }
  try {
    if (typeof window !== 'undefined') window.localStorage.setItem(key, '1');
  } catch {
    // The session and document guards still prevent duplicate effects.
  }
  try {
    storage()?.setItem(CORRELATION_STORAGE_KEY, outcome.correlationId);
  } catch {
    // The document guard still prevents duplicate effects.
  }
  return true;
};

// G2's OmniAuth upstream provider, mapped to the GA4 SSO detail values.
// The claim carries a provider for SSO logins only, so an absent provider is
// an email login: the unified-login sheet's form-submitted rows label those
// 'email'.
const IDP_FORM_STEP_DETAILS: Record<string, 'google' | 'linkedin' | 'email'> = {
  google_oauth2: 'google',
  google_business: 'google',
  linkedin: 'linkedin',
};

export const buildUnifiedLoginCompletion = (outcome: UnifiedLoginOutcome) => ({
  name: 'email_capture',
  form_type: 'uw',
  // The literal spec value, matching the CTA instrumentation's form_name;
  // the resolved outcome lives in form_submitted_result.
  form_name: 'sign-up | log-in',
  form_step_name: 'form-submitted',
  form_submitted_result: outcome.isSignUp ? 'sign-up' : 'log-in',
  form_step_details: IDP_FORM_STEP_DETAILS[outcome.provider ?? ''] ?? 'email',
  userId: outcome.userId,
  site: outcome.site,
  user_custom_session_id: outcome.correlationId,
  // Auth.js JWTs minted before method existed carry an outcome without it.
  login_method: outcome.method || 'unified_login',
});

let redirectInProgress: Promise<unknown> | null = null;
// A stopped navigation (Esc) fires no pageshow, so the guard must also expire.
const REDIRECT_GUARD_MS = 5000;
// A second click must not overwrite Auth.js's pending state/PKCE cookies.
export const runLoginRedirect = <T>(start: () => Promise<T>): Promise<T> => {
  if (redirectInProgress) return redirectInProgress as Promise<T>;
  const release = () => {
    if (redirectInProgress === attempt) redirectInProgress = null;
  };
  const attempt: Promise<T> = Promise.resolve()
    .then(start)
    .then(
      (result) => {
        setTimeout(release, REDIRECT_GUARD_MS);
        return result;
      },
      (error) => {
        release();
        throw error;
      }
    );
  redirectInProgress = attempt;
  return attempt;
};
if (typeof window !== 'undefined') {
  window.addEventListener('pageshow', () => {
    redirectInProgress = null;
  });
}
